export type AchievementTier = 'bronze' | 'silver' | 'gold' | 'platinum';

export interface TierRequirement {
    tier: AchievementTier;
    milestone: number;
    badgeColor: string;
    label: string;
}

export interface Achievement {
    id: string;
    name: string;
    subtitle: string;
    description: string;
    statKey: string; // Key in UserProfile.stats
    icon: string; // Emoji for now, can be mapping to SVG
    tiers: {
        bronze: number;
        silver: number;
        gold: number;
        platinum: number;
    };
}

export const ACHIEVEMENTS: Achievement[] = [
    {
        id: 'endless-rally',
        name: 'Endless Rally',
        subtitle: 'Every match played brings you closer to mastery.',
        description: 'Total number of matches played regardless of win or loss.',
        statKey: 'totalGamesPlayed',
        icon: '🎾',
        tiers: {
            bronze: 25,
            silver: 250,
            gold: 1000,
            platinum: 10000
        }
    },
    {
        id: 'victory-protocol',
        name: 'Victory Protocol',
        subtitle: 'Winning is the ultimate proof of skill.',
        description: 'Total number of match victories across all modes.',
        statKey: 'totalSoloWins_plus_multiplayerWins', // Special case handled in UI
        icon: '🏆',
        tiers: {
            bronze: 10,
            silver: 100,
            gold: 500,
            platinum: 5000
        }
    },
    {
        id: 'battle-hardened',
        name: 'Battle Hardened',
        subtitle: 'Every defeat sharpens your next victory.',
        description: 'Total matches lost, recognizing persistence.',
        statKey: 'totalSoloLosses_plus_multiplayerLosses', // Special case
        icon: '💀',
        tiers: {
            bronze: 10,
            silver: 100,
            gold: 500,
            platinum: 2000
        }
    },
    {
        id: 'training-regimen',
        name: 'Training Regimen',
        subtitle: 'Practice until the impossible becomes routine.',
        description: 'Number of matches played against AI opponents.',
        statKey: 'totalSoloWins_plus_totalSoloLosses', // Special case
        icon: '🤖',
        tiers: {
            bronze: 10,
            silver: 100,
            gold: 500,
            platinum: 5000
        }
    },
    {
        id: 'arena-challenger',
        name: 'Arena Challenger',
        subtitle: 'True skill is proven against real opponents.',
        description: 'Total multiplayer matches played.',
        statKey: 'multiplayerWins_plus_multiplayerLosses', // Special case
        icon: '⚔️',
        tiers: {
            bronze: 10,
            silver: 100,
            gold: 500,
            platinum: 5000
        }
    },
    {
        id: 'dominator',
        name: 'Dominator',
        subtitle: 'Rule the arena and leave rivals behind.',
        description: 'Multiplayer victories only.',
        statKey: 'multiplayerWins',
        icon: '👑',
        tiers: {
            bronze: 5,
            silver: 50,
            gold: 250,
            platinum: 2000
        }
    },
    {
        id: 'ascension',
        name: 'Ascension',
        subtitle: 'The higher you climb, the more legends notice.',
        description: 'Player level milestones.',
        statKey: 'level',
        icon: '🚀',
        tiers: {
            bronze: 10,
            silver: 25,
            gold: 50,
            platinum: 100
        }
    },
    {
        id: 'demon-slayer',
        name: 'Demon Slayer',
        subtitle: 'Few survive the highest difficulty.',
        description: 'Wins against Demon difficulty AI.',
        statKey: 'demonWins',
        icon: '👹',
        tiers: {
            bronze: 3,
            silver: 25,
            gold: 100,
            platinum: 1000
        }
    },

    {
        id: 'flawless-rally',
        name: 'Flawless Rally',
        subtitle: 'Win without letting the opponent score.',
        description: 'Perfect victories where opponent scores zero.',
        statKey: 'perfectWins',
        icon: '✨',
        tiers: {
            bronze: 3,
            silver: 25,
            gold: 100,
            platinum: 500
        }
    },
    // ── Adventure Mode Achievements ──────────────────────────────────────
    {
        id: 'adv-first-steps',
        name: 'Into the Pit',
        subtitle: 'Every legend starts somewhere.',
        description: 'Total Adventure Mode runs completed.',
        statKey: 'adventure.totalRuns',
        icon: '🥒',
        tiers: {
            bronze: 1,
            silver: 5,
            gold: 25,
            platinum: 100
        }
    },
    {
        id: 'adv-progression',
        name: 'Kitchen Explorer',
        subtitle: 'Survive deeper into the fermentation lab.',
        description: 'Highest Adventure Mode level reached in a single run.',
        statKey: 'adventure.bestLevel',
        icon: '🗺️',
        tiers: {
            bronze: 5,
            silver: 10,
            gold: 20,
            platinum: 50
        }
    },
    {
        id: 'adv-brick-destroyer',
        name: 'Brick Exterminator',
        subtitle: 'Nothing survives your baguette.',
        description: 'Total bricks destroyed across all Adventure Mode runs.',
        statKey: 'adventure.totalBricks',
        icon: '💥',
        tiers: {
            bronze: 50,
            silver: 250,
            gold: 500,
            platinum: 2000
        }
    }
];

export const getTier = (value: number, tiers: { bronze: number; silver: number; gold: number; platinum: number }): AchievementTier | null => {
    if (value >= tiers.platinum) return 'platinum';
    if (value >= tiers.gold) return 'gold';
    if (value >= tiers.silver) return 'silver';
    if (value >= tiers.bronze) return 'bronze';
    return null;
};

export const getNextMilestone = (value: number, tiers: { bronze: number; silver: number; gold: number; platinum: number }) => {
    if (value < tiers.bronze) return { milestone: tiers.bronze, tier: 'bronze' as AchievementTier };
    if (value < tiers.silver) return { milestone: tiers.silver, tier: 'silver' as AchievementTier };
    if (value < tiers.gold) return { milestone: tiers.gold, tier: 'gold' as AchievementTier };
    if (value < tiers.platinum) return { milestone: tiers.platinum, tier: 'platinum' as AchievementTier };
    return { milestone: tiers.platinum, tier: 'platinum' as AchievementTier }; // Already platinum
};
